import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, HeartHandshake, Loader2, Search, Shield, UserX } from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../auth";
import {
  AppHeader,
  AppStateScreen,
  BrandedLoading,
  MatchProfileCard,
  ProfilePhoto,
  StackNavLink,
} from "../components/app";
import type { AdminUserListItem, MatchProfile } from "../types";
import {
  isPermanentSuspendStop,
  ModerationSuspendPicker,
  suspendStopLabel,
  suspendStopDays,
  type SuspendStopIndex,
} from "./ModerationSuspendPicker";
import {
  defaultSuspendNoteFromReports,
  type AdminUserReport,
} from "./moderationReports";
import { ModerationMemberMatches } from "./ModerationMemberMatches";
import { ModerationSuspensionHistory } from "./ModerationSuspensionHistory";
import type { AdminMemberMatch } from "./moderationMatches";
import { enrichMemberMatchesWithReports } from "./moderationMatches";
import type { AdminSuspensionHistoryEntry } from "./moderationSuspensions";

type MemberFilter = "pending_approval" | "reported" | "suspended" | "all";

const MEMBER_FILTERS: ReadonlyArray<{ value: MemberFilter; label: string }> = [
  { value: "pending_approval", label: "Pending" },
  { value: "reported", label: "Reported" },
  { value: "suspended", label: "Suspended" },
  { value: "all", label: "All" },
];

function isStaff(role?: string) {
  return role === "admin" || role === "moderator";
}

export function RequireModerator({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <BrandedLoading message="Opening moderation" />;
  if (!user || !isStaff(user.role)) return <Navigate to="/app/match" replace />;
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <BrandedLoading message="Opening admin tools" />;
  if (!user || user.role !== "admin") return <Navigate to="/app/match" replace />;
  return <>{children}</>;
}

function banLabel(ban: AdminUserListItem["moderationBan"]) {
  if (ban === "permanent_ban") return "Suspended";
  if (ban === "temporary_ban") return "Suspended (temp)";
  return null;
}

function MemberAvatar({ user }: { user: AdminUserListItem }) {
  return (
    <div className="moderation-user-avatar" aria-hidden="true">
      <ProfilePhoto photoId={user.profilePictureId} />
    </div>
  );
}

function MemberBadges({ user }: { user: AdminUserListItem }) {
  return (
    <div className="moderation-user-badges">
      {!user.matchingApproved && !user.isBanned && (
        <span className="invite-lifecycle invite-lifecycle--pending">Pending</span>
      )}
      {user.moderationBan && (
        <span className="invite-lifecycle invite-lifecycle--expired">{banLabel(user.moderationBan)}</span>
      )}
      {user.pendingReportCount > 0 && (
        <span className="invite-lifecycle invite-lifecycle--accepted">
          {user.pendingReportCount} report{user.pendingReportCount === 1 ? "" : "s"}
        </span>
      )}
    </div>
  );
}

function InvitedBySubtitle({
  inviterId,
  inviterEmail,
}: {
  inviterId?: string | null;
  inviterEmail?: string | null;
}) {
  const navigate = useNavigate();
  const resolveInviter = useQuery({
    queryKey: ["admin-inviter-resolve", inviterEmail],
    queryFn: () => api.resolveAdminUser(inviterEmail!),
    enabled: Boolean(inviterEmail && !inviterId),
    retry: false,
  });

  if (!inviterEmail) return null;

  const targetId = inviterId ?? resolveInviter.data?.user.id;
  if (!targetId) {
    return <>Invited by {inviterEmail}</>;
  }

  return (
    <button
      type="button"
      className="profile-inviter-line text-link"
      onClick={(event) => {
        event.stopPropagation();
        navigate(`/admin/users/${targetId}`);
      }}
    >
      Invited by {inviterEmail}
    </button>
  );
}

function emptyStateCopy(filter: MemberFilter) {
  switch (filter) {
    case "pending_approval":
      return {
        title: "No pending approvals",
        body: "New signups waiting for review will appear here.",
      };
    case "reported":
      return {
        title: "No open reports",
        body: "Members with pending reports will appear here.",
      };
    case "suspended":
      return {
        title: "No suspended members",
        body: "Temporarily or permanently suspended accounts will appear here.",
      };
    default:
      return {
        title: "No members yet",
        body: "When people join Yara, they will appear in this list.",
      };
  }
}

export function AdminUsersPage() {
  const [filter, setFilter] = useState<MemberFilter>("all");
  const [search, setSearch] = useState("");

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-users", filter, search],
    queryFn: () => api.getAdminUsers({ filter, search }),
  });

  const users = data?.users ?? [];
  const emptyCopy = emptyStateCopy(filter);

  return (
    <RequireModerator>
      <AppHeader title="Moderation" backTo="/settings" historyBack />
      <div className="moderation-toolbar">
        <p className="moderation-toolbar-copy">
          Approve new members, review reports, and manage suspensions.
        </p>
        <div className="moderation-search-input">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search name or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search members"
          />
        </div>

        <div className="moderation-filter-chips" role="tablist" aria-label="Member filters">
          {MEMBER_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={filter === value}
              className={filter === value ? "selected" : ""}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="banner error moderation-error">
          {formatApiError(error)}
          <button type="button" className="text-link" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      )}

      {isLoading ? (
        <BrandedLoading message="Loading members" />
      ) : (
        <>
          <div className="invite-section-head">
            <h2>Members</h2>
            <span>{users.length}</span>
          </div>

          <div className="settings-list moderation-member-list">
            {users.map((user) => (
              <StackNavLink
                key={user.id}
                className="setting-row setting-row--rich moderation-member-row"
                to={`/admin/users/${user.id}`}
              >
                <MemberAvatar user={user} />
                <span className="setting-copy">
                  <strong>{user.firstName || "No profile yet"}</strong>
                  <small>{user.email}</small>
                  <MemberBadges user={user} />
                </span>
                <ChevronRight size={18} aria-hidden="true" />
              </StackNavLink>
            ))}

            {!error && users.length === 0 && (
              <AppStateScreen
                icon={Shield}
                title={emptyCopy.title}
                body={emptyCopy.body}
                tone="neutral"
              />
            )}
          </div>

          {isFetching && !isLoading && users.length > 0 && (
            <p className="moderation-refreshing" aria-live="polite">
              <Loader2 className="spin" size={16} /> Refreshing…
            </p>
          )}
        </>
      )}
    </RequireModerator>
  );
}

export function AdminUserDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [suspendStop, setSuspendStop] = useState<SuspendStopIndex>(0);
  const [suspendNote, setSuspendNote] = useState("");
  const [suspendNoteTouched, setSuspendNoteTouched] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-user", id],
    queryFn: () => api.getAdminUser(id),
    enabled: Boolean(id),
  });

  const {
    data: matchesData,
    isLoading: matchesLoading,
    error: matchesError,
    refetch: refetchMatches,
  } = useQuery({
    queryKey: ["admin-user-matches", id],
    queryFn: () => api.getAdminUserMatches(id),
    enabled: Boolean(id),
  });

  const approve = useMutation({
    mutationFn: () => api.approveAdminUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const suspend = useMutation({
    mutationFn: (body: ({ permanent: true } | { days: number }) & { note?: string }) =>
      api.suspendAdminUser(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const unsuspend = useMutation({
    mutationFn: () => api.unsuspendAdminUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-user", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const moderation = data?.user;
  const profile = data?.profile as MatchProfile | null | undefined;
  const memberReports = (data?.reports ?? []) as AdminUserReport[];
  const memberMatches = enrichMemberMatchesWithReports(
    (matchesData?.matches ?? data?.matches ?? []) as AdminMemberMatch[],
    memberReports,
  );
  const suspensionHistory = (data?.suspensionHistory ?? []) as AdminSuspensionHistoryEntry[];
  const actionError = approve.error || suspend.error || unsuspend.error;
  const isSelf = Boolean(currentUser?.id && moderation?.id === currentUser.id);
  const isStaffAccount = moderation?.role === "admin" || moderation?.role === "moderator";
  const canModerate = Boolean(moderation && !isSelf && !isStaffAccount);
  const actionableReportCount = memberReports.filter(
    (report) => report.status === "pending" || report.status === "investigating",
  ).length;
  const showStandaloneSuspend = canModerate && !moderation?.isBanned && actionableReportCount === 0;
  const isPermanentSuspension = moderation?.moderationBan === "permanent_ban";
  const canLiftSuspension = Boolean(
    canModerate && (!isPermanentSuspension || currentUser?.role === "admin"),
  );
  const memberSubtitle: ReactNode = moderation?.inviterEmail ? (
    <InvitedBySubtitle inviterId={moderation.inviterId} inviterEmail={moderation.inviterEmail} />
  ) : (
    moderation?.email
  );

  const suspendIsPermanent = isPermanentSuspendStop(suspendStop);

  useEffect(() => {
    setSuspendNoteTouched(false);
    setSuspendNote("");
  }, [id]);

  useEffect(() => {
    if (suspendNoteTouched) return;
    setSuspendNote(defaultSuspendNoteFromReports((data?.reports ?? []) as AdminUserReport[]));
  }, [data?.reports, suspendNoteTouched]);

  const onSuspend = () => {
    const note = suspendNote.trim() || undefined;
    if (isPermanentSuspendStop(suspendStop)) {
      if (!window.confirm("Permanently suspend this member from matching? They can still sign in, but only an admin can lift it later.")) {
        return;
      }
      suspend.mutate({ permanent: true, note });
      return;
    }

    const days = suspendStopDays(suspendStop);
    if (days) suspend.mutate({ days, note });
  };

  const moderationActions = moderation ? (
    <div className="moderation-action-stack account-stack">
      {canModerate && actionError && <div className="banner error">{formatApiError(actionError)}</div>}

      {!canModerate ? (
        <article className="account-card moderation-guard-card">
          <div className="account-card-head">
            <Shield />
            <div>
              <h2>{isSelf ? "Your account" : "Staff account"}</h2>
              <p>
                {isSelf
                  ? "You can't suspend or approve your own account from here."
                  : "Staff accounts can't be moderated from this panel."}
              </p>
            </div>
          </div>
        </article>
      ) : (
        <>
          {!moderation.matchingApproved && !moderation.isBanned && (
            <article className="account-card">
              <div className="account-card-head">
                <Check />
                <div>
                  <h2>Approve for matching</h2>
                  <p>Allow Yara to include this member in introductions.</p>
                </div>
              </div>
              <button
                type="button"
                className="button primary"
                disabled={approve.isPending}
                onClick={() => approve.mutate()}
              >
                <Check size={18} /> Approve member
              </button>
            </article>
          )}

          {currentUser?.role === "admin" && (
            <article className="account-card">
              <div className="account-card-head">
                <HeartHandshake />
                <div>
                  <h2>Manual introduction</h2>
                  <p>Select a second eligible member, then queue the pair or introduce them immediately.</p>
                </div>
              </div>
              <button
                type="button"
                className="button secondary"
                onClick={() => navigate(`/admin/manual-matches?member=${encodeURIComponent(moderation.id)}`)}
              >
                <HeartHandshake size={18} /> Open manual matching
              </button>
            </article>
          )}

          <ModerationMemberMatches
            memberId={moderation.id}
            matches={memberMatches}
            isLoading={matchesLoading}
            error={matchesError}
            onRetry={() => refetchMatches()}
          />

          {actionableReportCount > 0 && suspensionHistory.length > 0 && (
            <article className="account-card moderation-suspension-history-card">
              <ModerationSuspensionHistory entries={suspensionHistory} />
            </article>
          )}

          {moderation.isBanned ? (
            <article className="account-card">
              <div className="account-card-head">
                <UserX />
                <div>
                  <h2>{isPermanentSuspension ? "Lift permanent suspension" : "Lift suspension"}</h2>
                  <p>
                    {isPermanentSuspension
                      ? "Restore matching access for this member. They can sign in while suspended, but cannot be matched."
                      : "Restore access and allow this member back into matching."}
                  </p>
                </div>
              </div>
              {canLiftSuspension ? (
                <button
                  type="button"
                  className="button secondary"
                  disabled={unsuspend.isPending}
                  onClick={() => unsuspend.mutate()}
                >
                  {isPermanentSuspension ? "Lift permanent suspension" : "Lift suspension"}
                </button>
              ) : (
                <p className="moderation-admin-only-note">
                  Permanent suspensions can only be lifted by an admin.
                </p>
              )}
              <ModerationSuspensionHistory entries={suspensionHistory} />
            </article>
          ) : (
            showStandaloneSuspend && (
              <article className={`account-card moderation-suspend-card${suspendIsPermanent ? " danger-zone" : ""}`}>
                <div className="account-card-head">
                  <UserX />
                  <div>
                    <h2>Suspend member</h2>
                    <p>Choose how long to pause matching access, or suspend indefinitely.</p>
                  </div>
                </div>

                <ModerationSuspensionHistory entries={suspensionHistory} />

                <label className="field moderation-suspend-note">
                  <span>Suspension note</span>
                  <textarea
                    rows={3}
                    value={suspendNote}
                    onChange={(event) => {
                      setSuspendNoteTouched(true);
                      setSuspendNote(event.target.value);
                    }}
                    placeholder="Optional note for the audit log"
                  />
                </label>

                <ModerationSuspendPicker value={suspendStop} onChange={setSuspendStop} />
                <button
                  type="button"
                  className={`button ${suspendIsPermanent ? "danger" : "secondary"}`}
                  disabled={suspend.isPending}
                  onClick={onSuspend}
                >
                  <UserX size={18} />
                  {suspendIsPermanent ? "Suspend permanently" : `Suspend for ${suspendStopLabel(suspendStop)}`}
                </button>
              </article>
            )
          )}
        </>
      )}

      <button type="button" className="text-link moderation-back-link" onClick={() => navigate("/admin/users")}>
        Back to all members
      </button>
    </div>
  ) : null;

  return (
    <RequireModerator>
      <AppHeader title="Member review" backTo="/admin/users" desktopHidden edgeToEdge />
      {isLoading && <BrandedLoading message="Loading member" />}
      {error && (
        <div className="banner error moderation-error">
          {formatApiError(error)}
          <button type="button" className="text-link" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      )}

      {moderation && profile && (
        <MatchProfileCard
          profile={profile}
          kicker="Member review"
          subtitle={memberSubtitle}
          showGallery={false}
          connected
          className="moderation-profile-card"
        >
          {moderationActions}
        </MatchProfileCard>
      )}

      {moderation && !profile && !isLoading && (
        <div className="moderation-incomplete">
          <AppStateScreen
            icon={Shield}
            title="Profile not completed"
            body={
              canModerate
                ? "This member has not finished their profile yet. You can still approve or suspend the account."
                : "This account can't be moderated from this panel."
            }
            tone="neutral"
          />
          {moderationActions}
        </div>
      )}
    </RequireModerator>
  );
}
